/**
 * JS interop helpers: click-outside detection, scroll-lock, focus-trap.
 *
 * Everything here is published on `window.BlazingCommon`, and C# reaches each helper by its full
 * path as a string literal (`JSRuntime.InvokeVoidAsync("BlazingCommon.Utils.focusById", …)`). Those
 * paths are a runtime contract that neither compiler can check: renaming a group or a member here
 * builds cleanly, passes every test, and fails at the first call in the browser. Nothing on the
 * published object may be renamed, removed, or have its parameter order changed.
 *
 * This file is an ES module, so the host page must load it with `<script type="module" src="…">`.
 * A plain `<script src>` cannot parse a module and leaves `window.BlazingCommon` undefined, which
 * surfaces as a null reference at the first interop call rather than as a load error.
 *
 * Module scripts are deferred, so the assignment below runs after the document is parsed. Every C#
 * interop call happens after Blazor has started, which is later still, so the object is in place
 * before anything reaches for it.
 */

import {
    focusCustomDeclarations,
    focusInlineProperties,
    normaliseFocusMotion,
    normaliseFocusPreset,
    normaliseFocusReveal,
    parseFocusCustom,
    sanitiseFocusCustom,
    type FocusCustomValues
} from "./internal/blazing-focus-theme.js";
import { safeInvoke as guardedInvoke } from "./internal/blazing-interop.js";
import { revealScrollTop } from "./internal/blazing-list-reveal.js";
import { buildNumericPattern } from "./internal/blazing-numeric-pattern.js";
import { createElementRegistry } from "./internal/blazing-registry.js";
import { watchScrollRegions } from "./internal/blazing-scroll-region.js";
import { measureSliderTravel, type SliderTravel } from "./internal/blazing-slider-travel.js";

/**
 * An element that may carry a text selection, which only form fields do.
 *
 * The helpers below are handed element references from .NET and cannot know which kind of element
 * arrived, so each one tests for the member it needs rather than assuming a field.
 */
interface SelectableField extends HTMLElement {
    /** Selects the whole value, on the elements that have a value to select. */
    select?: () => void;

    /** The start of the current selection, or null on an element that has no selection. */
    selectionStart?: number | null;

    /** The end of the current selection, or null on an element that has no selection. */
    selectionEnd?: number | null;
}

/** A bounding rectangle in the shape .NET deserialises it into. */
interface ElementBounds {
    /** Distance from the viewport's left edge, in CSS pixels. */
    left: number;

    /** Distance from the viewport's top edge, in CSS pixels. */
    top: number;

    /** Width in CSS pixels. */
    width: number;

    /** Height in CSS pixels. */
    height: number;
}

/**
 * The keys whose default action is suppressed on each guarded element.
 *
 * The original guard was an expando on the element itself. A map keyed weakly by the element does
 * the same job without writing a property onto DOM the framework owns, and it forgets an element as
 * soon as the element is collected - which is why no cleanup call is needed.
 */
const guardedKeysByElement = new WeakMap<Element, Set<string>>();

/** The four arrow keys. */
const ARROW_KEYS: readonly string[] = ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight"];

/**
 * Suppresses the default action - page scroll, for the navigation keys - of the listed keys pressed on or
 * inside an element, without stopping the event, so the element's own key handlers still see every key.
 * One listener serves each element; a later call adds its keys to the ones already guarded.
 *
 * @param el The element to guard, or null when it is not rendered.
 * @param keys The KeyboardEvent.key values to suppress.
 * @param targetSelector When given, only a key pressed on an element matching it is suppressed, so a
 * control inside the guarded element (a text box in a templated row, say) keeps its own keys. Fixed by the
 * first call for an element.
 */
function guardElementKeys(el: HTMLElement | null, keys: readonly string[], targetSelector?: string | null): void {
    if (!el) return;

    const existing = guardedKeysByElement.get(el);
    if (existing) {
        for (const key of keys) existing.add(key);
        return;
    }

    const guarded = new Set(keys);
    guardedKeysByElement.set(el, guarded);
    el.addEventListener("keydown", (event) => {
        if (!guarded.has(event.key)) return;
        if (targetSelector && !(event.target instanceof Element && event.target.matches(targetSelector))) return;
        event.preventDefault();
    });
}

/** The anchors already given button-style Space activation, kept weakly for the same reason. */
const spaceActivatedElements = new WeakSet<Element>();

/** The in-page links already set to focus their target when followed, kept weakly for the same reason. */
const targetFocusingLinks = new WeakSet<Element>();

/** The regions whose navigation keys have already been kept from scrolling the page. */
const navigationKeyGuardedElements = new WeakSet<Element>();

/** The keys a paging or roving region handles itself. */
const NAVIGATION_KEYS: ReadonlySet<string> = new Set(["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Home", "End"]);

/** The elements the browser lets the user Tab to, before the visibility check. */
const TABBABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex]:not([tabindex="-1"]), [contenteditable="true"]';

/**
 * The elements that can take focus, whether or not the user can Tab to them: everything in TABBABLE plus
 * an element taken out of the Tab order with tabindex="-1", such as a roving item that arrow keys reach.
 */
const FOCUSABLE =
    'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), ' +
    'textarea:not([disabled]), [tabindex], [contenteditable="true"]';

/** A native control that is disabled: it takes no focus, yet it stays in the accessibility tree. */
const DISABLED_CONTROL = 'button:disabled, input:disabled, select:disabled, textarea:disabled';

/**
 * How many animation frames a list reveal waits for its list or option to be laid out before giving up.
 *
 * A list hosted in a top-layer popup renders in the popup's own subtree, after the component that asked
 * for the reveal has rendered, and an unshown popover has no layout to scroll. A small fixed budget covers
 * that gap without letting a list that never appears keep a frame callback alive.
 */
const REVEAL_FRAME_BUDGET = 10;

/** How long a list waits for a row it was asked to focus to be drawn before it stops watching, in milliseconds. */
const ROW_FOCUS_WAIT_MS = 2000;

/** Stops each list's wait for a row to focus, keyed by the list, so a later move replaces an earlier one. */
const pendingRowFocus = new WeakMap<HTMLElement, () => void>();

/**
 * Reports how much a list is scaled by a transform, such as a popup's entrance animation.
 *
 * Bounding boxes include the transform while scroll offsets do not, so a distance measured between two
 * boxes is divided by this before it is applied to `scrollTop`.
 *
 * @param list The scrolling list.
 * @returns The rendered height over the layout height, or 1 when the list has no layout height.
 */
function listScale(list: HTMLElement): number {
    return list.offsetHeight > 0 ? list.getBoundingClientRect().height / list.offsetHeight : 1;
}

/**
 * Measures where an element starts inside a list's scroll content, which scrolling does not change.
 *
 * @param list The scrolling list.
 * @param element An element inside the list.
 * @param scale The list's transform scale, from listScale.
 * @returns The element's start edge, in CSS pixels from the start of the list's scroll content.
 */
function scrollContentStart(list: HTMLElement, element: Element, scale: number): number {
    return (element.getBoundingClientRect().top - list.getBoundingClientRect().top) / scale - list.clientTop + list.scrollTop;
}

/**
 * Measures how much of a list's visible start is covered by pinned leading rows, such as a sticky search
 * box, so an option underneath one is not taken for visible. Only the rows before the first option or
 * Virtualize spacer are read.
 *
 * @param list The scrolling list.
 * @param scale The list's transform scale, from listScale.
 * @returns The covered extent, in CSS pixels from the list's inner start edge.
 */
function stickyLeadingInset(list: HTMLElement, scale: number): number {
    const listTop = list.getBoundingClientRect().top;
    let inset = 0;

    for (const child of Array.from(list.children)) {
        if (child.getAttribute("role") === "option" || !child.className) {
            break;
        }

        if (getComputedStyle(child).position === "sticky") {
            inset = Math.max(inset, (child.getBoundingClientRect().bottom - listTop) / scale - list.clientTop);
        }
    }

    return inset;
}

/**
 * Scrolls a list so the stretch of its content from itemStart to itemEnd is visible, moving only the list.
 *
 * @param list The scrolling list.
 * @param itemStart The stretch's start, in CSS pixels from the start of the list's scroll content.
 * @param itemEnd The stretch's end, in the same coordinates.
 * @param scale The list's transform scale, from listScale.
 * @param isCentred Whether an out-of-view stretch is centred rather than aligned with the nearer edge.
 */
function scrollListTo(list: HTMLElement, itemStart: number, itemEnd: number, scale: number, isCentred: boolean): void {
    const target = revealScrollTop(itemStart, itemEnd, list.scrollTop, list.clientHeight, isCentred, stickyLeadingInset(list, scale));

    if (target !== list.scrollTop) {
        list.scrollTop = target;
    }
}

// ──────────────── Utils ────────────────
const utils = {
    /**
     * Wraps a function so it runs only once calls have stopped arriving for the given delay.
     *
     * @param callback The function to defer. It is applied with the caller's `this` and arguments.
     * @param delay How long the calls must stop for, in milliseconds.
     * @returns The debounced function.
     */
    debounce(callback: (...args: unknown[]) => void, delay: number): (...args: unknown[]) => void {
        let timer: ReturnType<typeof setTimeout> | undefined;

        return function (this: unknown, ...args: unknown[]): void {
            clearTimeout(timer);
            timer = setTimeout(() => callback.apply(this, args), delay);
        };
    },

    /**
     * Reports the viewport width.
     *
     * @returns The window's inner width in CSS pixels.
     */
    getScreenWidth(): number {
        return window.innerWidth;
    },

    /**
     * Measures an element's box.
     *
     * @param element The element to measure.
     * @returns The element's position and size, relative to the viewport.
     */
    getElementBounds(element: HTMLElement): ElementBounds {
        const rect = element.getBoundingClientRect();

        return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    },

    /**
     * Measures a slider's inputs container and the diameter of its thumbs, so a click on the track can be
     * mapped across the stretch the thumbs' centres travel rather than across the whole container.
     *
     * @param element The slider's inputs container.
     * @returns The container's position and size, relative to the viewport, and the thumb diameter in CSS pixels.
     */
    getSliderTravel(element: HTMLElement): SliderTravel {
        return measureSliderTravel(element);
    },

    /**
     * Measures an element's layout size, which a transform does not change - unlike its bounding box,
     * which an entrance animation scaling the element reports smaller than the element settles at.
     *
     * Used by BlazingFlyout to clamp a panel it measures while the panel is still animating in.
     *
     * @param element The element to measure, or null when it is not rendered.
     * @returns The layout width and height, or null when there is no element.
     */
    getLayoutSize(element: HTMLElement | null): { width: number; height: number } | null {
        return element ? { width: element.offsetWidth, height: element.offsetHeight } : null;
    },

    /**
     * Scrolls an element into view with nearest semantics, doing nothing when it is not in the document.
     *
     * @param elementId The id of the element to reveal.
     */
    scrollElementIntoView(elementId: string): void {
        const element = document.getElementById(elementId);

        if (element && typeof element.scrollIntoView === "function") {
            element.scrollIntoView({ block: "nearest", inline: "nearest" });
        }
    },

    /**
     * Reveals an option inside its scrolling list by setting the list's own scroll offset, so neither the
     * page nor any other ancestor scrolls (`scrollIntoView` would scroll every scrollable ancestor).
     *
     * An option already wholly visible leaves the list where it is. An out-of-view option is centred when
     * isCentred is set (a list opening on its selection) or brought just inside the nearer edge otherwise
     * (a keyboard move). A list or option that is not laid out yet - a popup body still rendering, or a
     * popover not shown - is retried on the next animation frame, up to a small fixed number of frames.
     *
     * @param listElementId The id of the scrolling list element.
     * @param optionElementId The id of the option to reveal.
     * @param isCentred Whether an out-of-view option is centred rather than aligned with the nearer edge.
     * @param attemptsRemaining How many more frames to wait for the list and option; callers omit it.
     */
    revealListOption(listElementId: string, optionElementId: string, isCentred = false, attemptsRemaining = REVEAL_FRAME_BUDGET): void {
        const list = document.getElementById(listElementId);
        const option = document.getElementById(optionElementId);

        if (!list || !option || !list.contains(option) || list.clientHeight === 0) {
            if (attemptsRemaining > 0) {
                requestAnimationFrame(() => {
                    utils.revealListOption(listElementId, optionElementId, isCentred, attemptsRemaining - 1);
                });
            }

            return;
        }

        const scale = listScale(list);
        const start = scrollContentStart(list, option, scale);

        scrollListTo(list, start, start + option.getBoundingClientRect().height / scale, scale, isCentred);
    },

    /**
     * Reveals the selected option of the list inside each of several regions, by the rules of
     * revealListOption, for a component that composes lists whose ids it does not own. In each region the
     * first `[role="listbox"]` (the region itself when it is one) is the scrolling list, and its first
     * option with `aria-selected="true"` is revealed. A list with no selected option is left alone; a
     * region whose list is not rendered or not laid out yet is retried on the next animation frame, up to a
     * small fixed number of frames.
     *
     * @param regionIds The ids of the regions to reveal in.
     * @param isCentred Whether an out-of-view option is centred rather than aligned with the nearer edge.
     * @param attemptsRemaining How many more frames to wait for the lists; callers omit it.
     */
    revealSelectedOptions(regionIds: readonly string[], isCentred = true, attemptsRemaining = REVEAL_FRAME_BUDGET): void {
        const waiting: string[] = [];

        for (const regionId of regionIds) {
            const region = document.getElementById(regionId);
            const list = region?.matches('[role="listbox"]') ? region : region?.querySelector<HTMLElement>('[role="listbox"]');

            if (!list || list.clientHeight === 0) {
                waiting.push(regionId);
                continue;
            }

            const option = list.querySelector('[role="option"][aria-selected="true"]');

            if (option) {
                const scale = listScale(list);
                const start = scrollContentStart(list, option, scale);

                scrollListTo(list, start, start + option.getBoundingClientRect().height / scale, scale, isCentred);
            }
        }

        if (waiting.length > 0 && attemptsRemaining > 0) {
            requestAnimationFrame(() => {
                utils.revealSelectedOptions(waiting, isCentred, attemptsRemaining - 1);
            });
        }
    },

    /**
     * Scrolls a virtualized list so the row at rowIndex is visible, setting the list's own scroll offset so
     * neither the page nor any other ancestor scrolls.
     *
     * When the row is already rendered it is measured directly. Otherwise its position is computed as
     * spacerOffset + rowIndex * itemSize, where the leading `Virtualize` spacer is deterministically the
     * first classless child of the list (every first-party row carries a `blz-*` class; Virtualize spacers
     * render with only a style attribute). The spacer offset also absorbs any static leading rows (search
     * box, select-all, column headers). The visibility and centring rules, and the retry while the list has
     * no layout yet, are those of revealListOption.
     *
     * @param listElementId The id of the scrolling list element.
     * @param rowIndex The index of the row to reveal.
     * @param itemSize The fixed row height the list was virtualized with, in CSS pixels.
     * @param optionElementId The id the row carries when it happens to be rendered already.
     * @param isCentred Whether an out-of-view row is centred rather than aligned with the nearer edge.
     * @param attemptsRemaining How many more frames to wait for the list; callers omit it.
     */
    scrollListToIndex(
        listElementId: string,
        rowIndex: number,
        itemSize: number,
        optionElementId: string,
        isCentred = false,
        attemptsRemaining = REVEAL_FRAME_BUDGET
    ): void {
        const list = document.getElementById(listElementId);

        if (!list || list.clientHeight === 0) {
            if (attemptsRemaining > 0) {
                requestAnimationFrame(() => {
                    utils.scrollListToIndex(listElementId, rowIndex, itemSize, optionElementId, isCentred, attemptsRemaining - 1);
                });
            }

            return;
        }

        const scale = listScale(list);
        const option = document.getElementById(optionElementId);

        if (option && list.contains(option)) {
            const optionStart = scrollContentStart(list, option, scale);

            scrollListTo(list, optionStart, optionStart + option.getBoundingClientRect().height / scale, scale, isCentred);

            return;
        }

        const spacer = Array.from(list.children).find((child) => !child.className);
        const top = (spacer ? scrollContentStart(list, spacer, scale) : 0) + rowIndex * itemSize;

        scrollListTo(list, top, top + itemSize, scale, isCentred);
    },

    /**
     * Moves focus to a row of a virtualized list as soon as the list draws it, for a keyboard move to a row
     * that is not drawn yet.
     *
     * Scrolling to that row makes the window drop the row that still holds focus, and until the new row is
     * focused a key pressed lands on the page and is lost. A mutation observer runs in the same task that
     * applies the render drawing the row, so focus moves before any further key is handled. Focus is taken
     * only while it is still inside the list or has fallen out of the document's controls, never from
     * something else the user has moved to. A later call for the same list replaces the earlier one, and a
     * row the window never draws stops being waited for after a short time.
     *
     * @param listElementId The id of the scrolling list element.
     * @param rowElementId The id of the focusable row.
     */
    focusRowWhenDrawn(listElementId: string, rowElementId: string): void {
        const list = document.getElementById(listElementId);

        if (!list) {
            return;
        }

        pendingRowFocus.get(list)?.();

        const tryFocus = (): boolean => {
            const row = document.getElementById(rowElementId);

            if (!row || !list.contains(row)) {
                return false;
            }

            const active = document.activeElement;

            if (active !== row && (!active || active === document.body || list.contains(active))) {
                row.focus({ preventScroll: true });
            }

            return true;
        };

        if (tryFocus()) {
            return;
        }

        const observer = new MutationObserver(() => {
            if (tryFocus()) {
                stop();
            }
        });
        const timer = window.setTimeout(() => stop(), ROW_FOCUS_WAIT_MS);
        const stop = (): void => {
            observer.disconnect();
            window.clearTimeout(timer);
            pendingRowFocus.delete(list);
        };

        observer.observe(list, { childList: true, subtree: true });
        pendingRowFocus.set(list, stop);
    },

    /**
     * Reports the responsive tier the viewport currently falls in.
     *
     * @returns One of "xs", "sm", "md", "lg" or "xl".
     */
    getCurrentBreakpoint(): string {
        const width = window.innerWidth;

        if (width < 576) return "xs";
        if (width < 768) return "sm";
        if (width < 992) return "md";
        if (width < 1200) return "lg";

        return "xl";
    },

    /**
     * Calls a .NET instance method, tolerating a reference the .NET side has already disposed.
     *
     * A reference torn down between an event arriving and its callback running is ordinary teardown
     * and is swallowed. The call is routed through the shared guarded invoke, which is the only
     * place in the solution that reaches `invokeMethodAsync` directly.
     *
     * @param dotNetRef The handle to the .NET object, or null when the caller no longer holds one.
     * @param method The name of the [JSInvokable] method to call.
     * @param args The arguments to forward.
     * @returns What the .NET method returned, or undefined when the call was skipped or failed.
     */
    safeInvoke(dotNetRef: DotNetObjectReference | null | undefined, method: string, ...args: unknown[]): Promise<unknown> {
        return guardedInvoke<unknown>(dotNetRef, method, undefined, ...args);
    },

    /**
     * Sets the (attribute-less) indeterminate DOM property on a checkbox. Used by BlazingCheckBox.
     *
     * @param element The checkbox input, or null when the component has not rendered it yet.
     * @param indeterminate Whether the box should paint the mixed state.
     */
    setCheckboxIndeterminate(element: HTMLInputElement | null, indeterminate: boolean): void {
        if (element) {
            element.indeterminate = !!indeterminate;
        }
    },

    /**
     * Selects all text inside a text input or textarea element. Used by BlazingTextBox.SelectAsync.
     *
     * @param element The field whose value to select, or null when it is not rendered.
     */
    selectElementText(element: SelectableField | null): void {
        if (element && typeof element.select === "function") {
            element.select();
        }
    },

    /**
     * Resizes a textarea to fit its content, capped at maxHeightPx (0 = uncapped).
     *
     * Used by BlazingTextArea auto-size.
     *
     * @param element The textarea to resize, or null when it is not rendered.
     * @param maxHeightPx The height ceiling in pixels, or 0 for no ceiling.
     * @returns The applied height in pixels.
     */
    autoSizeTextArea(element: HTMLTextAreaElement | null, maxHeightPx: number): number {
        if (!element) return 0;

        element.style.height = "auto";

        let next = element.scrollHeight;

        if (maxHeightPx > 0 && next > maxHeightPx) {
            next = maxHeightPx;
            element.style.overflowY = "auto";
        } else {
            element.style.overflowY = "hidden";
        }

        element.style.height = next + "px";

        return next;
    },

    /**
     * Captures the pointer on el (touch + mouse drag parity) and returns its bounding rect.
     *
     * Used by BlazingCubicBezierEditor to track a control-point drag beyond the element bounds.
     *
     * @param el The element to capture the pointer on, or null when it is not rendered.
     * @param pointerId The id of the pointer to capture.
     * @returns The element's bounding rectangle, or a unit rectangle when there is no element.
     */
    grabPointer(el: HTMLElement | null, pointerId: number): ElementBounds {
        if (!el) return { left: 0, top: 0, width: 1, height: 1 };

        try { el.setPointerCapture?.(pointerId); } catch { /* best-effort */ }

        const rect = el.getBoundingClientRect();

        return { left: rect.left, top: rect.top, width: rect.width, height: rect.height };
    },

    /**
     * Releases a pointer capture previously taken by grabPointer. Used by BlazingCubicBezierEditor.
     *
     * @param el The element the pointer was captured on, or null when it is gone.
     * @param pointerId The id of the pointer to release.
     */
    releasePointer(el: HTMLElement | null, pointerId: number): void {
        try { el?.releasePointerCapture?.(pointerId); } catch { /* already released */ }
    },

    /**
     * Suppresses page scroll for arrow keys on el, leaving Tab and the rest to pass through so focus
     * is never trapped.
     *
     * Used by BlazingCubicBezierEditor handles. Idempotent, and the listener dies with the element so
     * no cleanup is needed.
     *
     * @param el The element to guard, or null when it is not rendered.
     */
    preventArrowScroll(el: HTMLElement | null): void {
        guardElementKeys(el, ARROW_KEYS);
    },

    /**
     * Suppresses the default action of the listed keys pressed on or inside an element - typically the
     * arrow, Home, End and Page keys of a widget that moves with them, so they do not also scroll the
     * page - without stopping the event, so every key handler still sees the key.
     *
     * Idempotent per element: a repeat call adds its keys to those already guarded instead of adding a
     * second listener. The listener dies with the element, so no cleanup is needed.
     *
     * @param el The element to guard, or null when it is not rendered.
     * @param keys The KeyboardEvent.key values to suppress.
     * @param targetSelector When given, only a key pressed on an element matching it is suppressed, so a
     * control inside the guarded element keeps its own keys; callers that guard every key omit it.
     */
    guardKeys(el: HTMLElement | null, keys: string[], targetSelector?: string | null): void {
        guardElementKeys(el, keys, targetSelector);
    },

    /**
     * Makes each region inside an element that matches a selector a Tab stop exactly while it scrolls, so
     * a scrolling region that holds nothing focusable can still be scrolled from the keyboard, and one that
     * fits gains no empty stop. The browser decides on its own as regions resize, open or fill; nothing is
     * reported to .NET. A region the author made focusable keeps its own tabindex.
     *
     * Idempotent per element, and the watch ends by itself once the element leaves the document.
     *
     * @param el The container, or null when it is not rendered.
     * @param selector The regions to watch, matched from the container.
     * @param watchedAttributes Attributes whose change inside the container can change a region's
     * overflow, such as an animation marker.
     */
    focusScrollingRegions(el: HTMLElement | null, selector: string, watchedAttributes?: string[] | null): void {
        watchScrollRegions(el, selector, watchedAttributes ?? []);
    },
    /**
     * Gives an element that announces the button role but is natively a link the Space activation a
     * button has: Space no longer scrolls the page, and its release clicks the element, which runs the
     * element's click handlers and follows its address exactly as Enter does.
     *
     * Used by BlazingButton's link form. An element marked aria-disabled="true" is left inert. Idempotent,
     * and the listeners die with the element so no cleanup is needed.
     *
     * @param el The element to activate, or null when it is not rendered.
     */
    activateOnSpace(el: HTMLElement | null): void {
        if (!el || spaceActivatedElements.has(el)) return;

        spaceActivatedElements.add(el);
        el.addEventListener("keydown", (event) => {
            if (event.key === " ") event.preventDefault();
        });
        el.addEventListener("keyup", (event) => {
            if (event.key !== " " || el.getAttribute("aria-disabled") === "true") return;

            event.preventDefault();
            el.click();
        });
    },

    /**
     * Makes an in-page link move focus to the element its fragment names when it is followed, in the same
     * turn as the activation. A browser does this for a plain fragment link, but a router that takes over
     * the link only scrolls, and the next Tab would then start from the link instead of the target.
     *
     * Used by BlazingLayout's skip link. The target must accept focus (a tabindex of -1 is enough).
     * Idempotent, and the listener dies with the element so no cleanup is needed.
     *
     * @param el The link, or null when it is not rendered.
     */
    focusFragmentTargetOnFollow(el: HTMLAnchorElement | null): void {
        if (!el || targetFocusingLinks.has(el)) return;

        targetFocusingLinks.add(el);
        el.addEventListener("click", () => {
            const id = decodeURIComponent(new URL(el.href, document.baseURI).hash.slice(1));
            const target = id ? document.getElementById(id) : null;
            target?.focus({ preventScroll: true });
        });
    },

    /**
     * Moves focus to the first, or the last, element the user can Tab to inside a region, without
     * scrolling it into view, so a jump already under way keeps its own animation.
     *
     * Used by BlazingScrollTo when a keyboard jump to the start or the end hides the focused button:
     * focus moves to the destination instead of falling to the page body.
     *
     * @param containerId The id of the region, or null to use the selector or the whole document.
     * @param selector A selector for the region when no id is given, or null for the whole document.
     * @param fromEnd True to focus the last tabbable element, false for the first.
     * @param excludeId The id of a subtree to skip, typically the control that is being hidden.
     * @returns True when an element received focus.
     */
    focusTabbableEdge(containerId: string | null, selector: string | null, fromEnd: boolean, excludeId: string | null): boolean {
        const scope = (containerId ? document.getElementById(containerId) : null)
            ?? (selector ? document.querySelector<HTMLElement>(selector) : null)
            ?? document.body;
        const excluded = excludeId ? document.getElementById(excludeId) : null;
        const candidates = Array.from(scope.querySelectorAll<HTMLElement>(TABBABLE)).filter(
            (candidate) => candidate.offsetParent !== null && !excluded?.contains(candidate),
        );
        const target = fromEnd ? candidates.at(-1) : candidates[0];
        if (!target) return false;

        target.focus({ preventScroll: true });

        return document.activeElement === target;
    },

    /**
     * Focuses the first control a user can Tab to inside an element, or the element itself when it holds
     * none - for a wrapper that never takes focus itself but surrounds the control that should.
     *
     * Used by BlazingTooltip to return focus to its trigger when a sticky popover closes.
     *
     * @param el The wrapper, or null when it is not rendered.
     * @returns True when an element received focus.
     */
    focusFirstTabbable(el: HTMLElement | null): boolean {
        if (!el) return false;

        const target = Array.from(el.querySelectorAll<HTMLElement>(TABBABLE))
            .find((candidate) => candidate.offsetParent !== null) ?? el;
        target.focus();

        return document.activeElement === target;
    },

    /**
     * Adds an id to the aria-describedby of the control a user reaches inside a wrapper, keeping any
     * description the control already has. The first control the user can Tab to is preferred; failing that,
     * the first control that can take focus at all, such as a roving item with tabindex="-1" that arrow keys
     * reach; failing that, a disabled native control, which a reader still meets; and only when the wrapper
     * holds none of these, the wrapper itself. The control that takes focus is what a screen reader
     * describes, so a description on a wrapper around it is never read, and a wrapper carrying one inside a
     * menu bar or a tab list becomes an item those containers must not own.
     *
     * Used by BlazingTooltip to describe its trigger by the balloon text. Idempotent.
     *
     * @param el The wrapper, or null when it is not rendered.
     * @param descriptionId The id of the element that describes the control.
     */
    linkDescription(el: HTMLElement | null, descriptionId: string): void {
        if (!el || !descriptionId) return;

        const focusable = Array.from(el.querySelectorAll<HTMLElement>(FOCUSABLE));
        const target = focusable.find((candidate) => candidate.tabIndex >= 0)
            ?? focusable[0]
            ?? el.querySelector<HTMLElement>(DISABLED_CONTROL)
            ?? el;
        const ids = (target.getAttribute("aria-describedby") ?? "").split(/\s+/).filter((id) => id.length > 0);

        if (!ids.includes(descriptionId)) {
            ids.push(descriptionId);
            target.setAttribute("aria-describedby", ids.join(" "));
        }
    },

    /**
     * Suppresses page scroll for the arrow, Home and End keys pressed inside a region that pages or moves
     * with them, while leaving a key typed into a text field, a spin button or editable content to that
     * control.
     *
     * Used by BlazingPagination's navigation region. Idempotent, and the listener dies with the element so
     * no cleanup is needed.
     *
     * @param el The region to guard, or null when it is not rendered.
     */
    preventNavigationKeyScroll(el: HTMLElement | null): void {
        if (!el || navigationKeyGuardedElements.has(el)) return;

        navigationKeyGuardedElements.add(el);
        el.addEventListener("keydown", (event) => {
            if (!NAVIGATION_KEYS.has(event.key)) return;

            const target = event.target;
            if (target instanceof HTMLElement
                && (target.isContentEditable || target.matches("input, textarea, select, [role='spinbutton']"))) {
                return;
            }

            event.preventDefault();
        });
    },

    /**
     * Puts focus back inside a region when it has been lost - to the page body, to an element that has
     * left the page, or to a control that has just been disabled, which the browser is about to blur.
     *
     * Used by BlazingPagination after a page change disables the button that held focus. Does nothing
     * while focus is anywhere usable, so it never takes focus from where the user has put it.
     *
     * @param region The region to restore focus into, or null when it is not rendered.
     * @param preferredSelector A selector for the preferred target inside the region, or null.
     * @returns True when focus was moved, so a caller that reacts to the focus it causes can tell.
     */
    restoreLostFocus(region: HTMLElement | null, preferredSelector: string | null): boolean {
        if (!region) return false;

        const active = document.activeElement;
        const isLost = !active
            || active === document.body
            || !active.isConnected
            || (active instanceof HTMLButtonElement && active.disabled);

        if (!isLost) return false;

        const preferred = preferredSelector ? region.querySelector<HTMLElement>(preferredSelector) : null;
        const fallback = Array.from(region.querySelectorAll<HTMLElement>(TABBABLE))
            .find((candidate) => candidate.offsetParent !== null);
        const target = preferred ?? fallback;
        target?.focus();

        return target !== undefined && target !== null && document.activeElement === target;
    }
};

// ──────────────── Interop ────────────────
/**
 * The registrations whose priming answer is currently on the stack.
 *
 * Re-entry is detected on the stack rather than by counting, so there is no threshold to tune and
 * nothing to false-positive on.
 */
const primingRegistrations = new Set<string>();

/**
 * The re-entrancy guard the browser modules prime their owners through.
 *
 * A registration that answers its owner immediately - "priming" - is the shape behind the worst
 * failure this interop layer can produce. On WebAssembly a call from JS into .NET runs synchronously
 * inside the .NET call that is still awaiting the registration, so a component that records itself as
 * registered only after that await is still recorded as unregistered when its own priming answer
 * arrives. It registers again, primes again, and the main thread never reaches the event loop: the tab
 * stops painting and accepting input, then the renderer is killed. On Blazor Server the same answer is
 * marshalled back over the circuit and arrives after the await completes, so the ordering never shows
 * and the defect ships looking tested.
 *
 * Priming through this helper turns that runaway into an immediate, named error: a second
 * registration under the same id, raised while the first one's answer is still running, can only be
 * the loop.
 */
const interop = {
    /**
     * Answers a freshly registered owner, refusing an answer that is re-entering its own registration.
     *
     * @param id The registration id being primed.
     * @param description The module and function priming, used in the error message.
     * @param answer Invokes the owner; any return value is ignored.
     */
    prime(id: string, description: string, answer: () => void): void {
        if (primingRegistrations.has(id)) {
            throw new Error(
                `Blazing.UI: runaway interop loop in ${description}. Registration "${id}" was made again ` +
                "while its own priming answer was still running, which repeats without end and wedges " +
                "the browser tab. The owning component records itself as registered only after the " +
                "await that registers it; on WebAssembly the answer arrives before that line runs. " +
                "Record the registration before the interop call instead, and undo it if the call faults.");
        }

        primingRegistrations.add(id);

        try {
            answer();
        } finally {
            primingRegistrations.delete(id);
        }
    }
};

// ──────────────── ResponsiveService ────────────────
/** The debounced resize listener while one is attached, and null once it has been taken off. */
let responsiveResizeHandler: ((...args: unknown[]) => void) | null = null;

/** Feeds the viewport width to a .NET service on resize. */
const responsiveService = {
    /**
     * Starts reporting the viewport width, and reports it once immediately.
     *
     * @param dotNetRef The .NET service to report widths to.
     */
    init(dotNetRef: DotNetObjectReference): void {
        const debouncedHandler = utils.debounce(() => {
            void guardedInvoke(dotNetRef, "UpdateScreenWidthAsync", isReportingWidth, window.innerWidth);
        }, 150);

        responsiveResizeHandler = debouncedHandler;
        window.addEventListener("resize", debouncedHandler);
        // Report initial width
        void guardedInvoke(dotNetRef, "UpdateScreenWidthAsync", isReportingWidth, window.innerWidth);
    },

    /** Stops reporting the viewport width. Safe to call when nothing is attached. */
    dispose(): void {
        if (responsiveResizeHandler) {
            window.removeEventListener("resize", responsiveResizeHandler);
            responsiveResizeHandler = null;
        }
    }
};

/**
 * Reports whether width reporting is still attached.
 *
 * This is what separates ordinary teardown from a genuine fault in the .NET handler: after dispose
 * there is nothing to report to and a failure is expected, while a failure raised on a live
 * registration is the consumer's own defect and is surfaced.
 *
 * @returns True while a resize listener is attached.
 */
function isReportingWidth(): boolean {
    return responsiveResizeHandler !== null;
}

// ──────────────── ThemeService ────────────────
/** Applies and persists the page-level theme, accent, palette and background choices. */
const themeService = {
    /** The local-storage key the chosen theme mode is persisted under. */
    STORAGE_KEY: "blazing-theme",

    /** The local-storage key the chosen accent is persisted under. */
    ACCENT_STORAGE_KEY: "blazing-accent",

    /** The local-storage key the chosen neutral palette is persisted under. */
    PALETTE_STORAGE_KEY: "blazing-palette",

    /** The local-storage key the chosen background level is persisted under. */
    BACKGROUND_STORAGE_KEY: "blazing-bg-level",

    /** The local-storage key the chosen focus-indicator preset is persisted under. */
    FOCUS_STORAGE_KEY: "blazing-focus",

    /** The local-storage key the chosen focus motion is persisted under. */
    FOCUS_MOTION_STORAGE_KEY: "blazing-focus-motion",

    /** The local-storage key the chosen focus reveal wipe is persisted under. */
    FOCUS_REVEAL_STORAGE_KEY: "blazing-focus-reveal",

    /** The local-storage key the free-form focus choices (colour, opacity, border) are persisted under, as JSON. */
    FOCUS_CUSTOM_STORAGE_KEY: "blazing-focus-custom",

    /**
     * Applies a theme mode to the document and persists the choice.
     *
     * @param theme The mode name: "LIGHT", "DARK" or "SYSTEM".
     */
    applyTheme(theme: string): void {
        // theme: "LIGHT" | "DARK" | "SYSTEM"
        const root = document.documentElement;
        const mode = theme.toLowerCase();

        if (mode === "system") {
            delete root.dataset["theme"]; // fall through to prefers-color-scheme
        } else {
            root.dataset["theme"] = mode; // matches themes/dark.css [data-theme="dark"]
        }

        try {
            localStorage.setItem(this.STORAGE_KEY, theme);
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted theme mode.
     *
     * @returns The persisted mode name, or "SYSTEM" when none is stored or storage is unavailable.
     */
    getSavedTheme(): string {
        try {
            return localStorage.getItem(this.STORAGE_KEY) || "SYSTEM";
        } catch {
            return "SYSTEM";
        }
    },

    /**
     * Applies an accent to the document and persists the choice.
     *
     * @param accent The accent name, or null for the default accent.
     */
    applyAccent(accent: string | null): void {
        // accent: "teams" | "blue" | … | null (default)
        const root = document.documentElement;

        if (!accent || accent === "default") {
            delete root.dataset["accent"];
        } else {
            root.dataset["accent"] = accent;
        }

        try {
            localStorage.setItem(this.ACCENT_STORAGE_KEY, accent ?? "default");
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted accent.
     *
     * @returns The persisted accent name, or "default" when none is stored or storage is unavailable.
     */
    getSavedAccent(): string {
        try {
            return localStorage.getItem(this.ACCENT_STORAGE_KEY) || "default";
        } catch {
            return "default";
        }
    },

    /**
     * Applies a neutral palette to the document and persists the choice.
     *
     * @param palette The palette name, or null for the neutral palette.
     */
    applyPalette(palette: string | null): void {
        // palette: "slate" | "gray" | "zinc" | "stone" | "taupe" | "mauve" | "mist" | "olive" | null (neutral)
        const root = document.documentElement;

        if (!palette || palette === "neutral") {
            delete root.dataset["palette"];
        } else {
            root.dataset["palette"] = palette;
        }

        try {
            localStorage.setItem(this.PALETTE_STORAGE_KEY, palette ?? "neutral");
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted neutral palette.
     *
     * @returns The persisted palette name, or "neutral" when none is stored or storage is unavailable.
     */
    getSavedPalette(): string {
        try {
            return localStorage.getItem(this.PALETTE_STORAGE_KEY) || "neutral";
        } catch {
            return "neutral";
        }
    },

    /**
     * Applies a background level to the document and persists the choice.
     *
     * @param level The level, or null for the default level.
     */
    applyBackgroundLevel(level: string | null): void {
        // level: "1" (default) | "2" | "3" | "4" - re-points the page background per theme
        const root = document.documentElement;

        if (!level || level === "1") {
            delete root.dataset["bgLevel"];
        } else {
            root.dataset["bgLevel"] = level;
        }

        try {
            localStorage.setItem(this.BACKGROUND_STORAGE_KEY, level ?? "1");
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted background level.
     *
     * @returns The persisted level, or "1" when none is stored or storage is unavailable.
     */
    getSavedBackgroundLevel(): string {
        try {
            return localStorage.getItem(this.BACKGROUND_STORAGE_KEY) || "1";
        } catch {
            return "1";
        }
    },

    /**
     * Applies a focus-indicator preset to the document and persists the choice.
     *
     * @param preset The preset name: "ring", "outline", "halo", "underline" or "inset". Any other value applies "ring".
     */
    applyFocusPreset(preset: string): void {
        const value = normaliseFocusPreset(preset) ?? "ring";
        document.documentElement.dataset["focus"] = value; // matches css/blazing-focus.css [data-focus]

        try {
            localStorage.setItem(this.FOCUS_STORAGE_KEY, value);
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted focus-indicator preset.
     *
     * @returns The persisted preset, or null when none is stored, the stored value names no preset, or
     *          storage is unavailable - so a caller can tell "the user chose" from "use the host default".
     */
    getSavedFocusPreset(): string | null {
        try {
            return normaliseFocusPreset(localStorage.getItem(this.FOCUS_STORAGE_KEY));
        } catch {
            return null;
        }
    },

    /**
     * Applies a focus motion to the document and persists the choice.
     *
     * @param motion The motion name: "off", "fast", "normal" or "slow". Any other value applies "fast",
     *               the default, which is expressed by removing the attribute.
     */
    applyFocusMotion(motion: string): void {
        const value = normaliseFocusMotion(motion) ?? "fast";
        const root = document.documentElement;

        if (value === "fast") {
            delete root.dataset["focusMotion"];
        } else {
            root.dataset["focusMotion"] = value; // matches css/blazing-focus.css [data-focus-motion]
        }

        try {
            localStorage.setItem(this.FOCUS_MOTION_STORAGE_KEY, value);
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted focus motion.
     *
     * @returns The persisted motion, or "fast" when none is stored, the stored value names no motion, or
     *          storage is unavailable.
     */
    getSavedFocusMotion(): string {
        try {
            return normaliseFocusMotion(localStorage.getItem(this.FOCUS_MOTION_STORAGE_KEY)) ?? "fast";
        } catch {
            return "fast";
        }
    },

    /**
     * Applies a focus reveal wipe to the document and persists the choice.
     *
     * @param reveal The reveal name: "none" or one of the wipe directions in `focusReveals`. Any other
     *               value applies "none", the default, which is expressed by removing the attribute.
     */
    applyFocusReveal(reveal: string): void {
        const value = normaliseFocusReveal(reveal) ?? "none";
        const root = document.documentElement;

        if (value === "none") {
            delete root.dataset["focusReveal"];
        } else {
            root.dataset["focusReveal"] = value; // read by js/blazing-focus-reveal.js as [data-focus-reveal]
        }

        try {
            localStorage.setItem(this.FOCUS_REVEAL_STORAGE_KEY, value);
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted focus reveal wipe.
     *
     * @returns The persisted reveal, or "none" when none is stored, the stored value names no reveal, or
     *          storage is unavailable.
     */
    getSavedFocusReveal(): string {
        try {
            return normaliseFocusReveal(localStorage.getItem(this.FOCUS_REVEAL_STORAGE_KEY)) ?? "none";
        } catch {
            return "none";
        }
    },

    /**
     * Applies the free-form focus choices to the document and persists them.
     *
     * Every focus property previously set inline is removed first, so a member left out of the new
     * choices returns to the stylesheet default. A value that is not valid for its member is dropped.
     *
     * @param values The colour, opacity and border choices, or null to clear them all and forget them.
     */
    applyFocusCustom(values: FocusCustomValues | null): void {
        const isColor = (value: string): boolean => CSS.supports("color", value);
        const accepted: FocusCustomValues = values ? sanitiseFocusCustom(values, isColor) : {};
        const style = document.documentElement.style;

        for (const property of focusInlineProperties) {
            style.removeProperty(property);
        }

        for (const [property, value] of focusCustomDeclarations(accepted, isColor)) {
            style.setProperty(property, value);
        }

        try {
            if (Object.keys(accepted).length === 0) {
                localStorage.removeItem(this.FOCUS_CUSTOM_STORAGE_KEY);
            } else {
                localStorage.setItem(this.FOCUS_CUSTOM_STORAGE_KEY, JSON.stringify(accepted));
            }
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reads the persisted free-form focus choices.
     *
     * @returns The persisted choices, or an empty object when none are stored, the entry is corrupt, or
     *          storage is unavailable.
     */
    getSavedFocusCustom(): FocusCustomValues {
        try {
            return parseFocusCustom(localStorage.getItem(this.FOCUS_CUSTOM_STORAGE_KEY));
        } catch {
            return {};
        }
    },

    /**
     * Forgets every focus choice and returns the document to the given default preset.
     *
     * The stored preset, motion, reveal and free-form choices are removed, every inline focus property and
     * the motion and reveal attributes are cleared, and the default is applied without being persisted - so a later page
     * load starts from the host's default again rather than from a stored copy of it.
     *
     * @param defaultPreset The host's default preset. Any value that names no preset applies "ring".
     */
    resetFocus(defaultPreset: string): void {
        const root = document.documentElement;

        for (const property of focusInlineProperties) {
            root.style.removeProperty(property);
        }

        delete root.dataset["focusMotion"];
        delete root.dataset["focusReveal"];
        root.dataset["focus"] = normaliseFocusPreset(defaultPreset) ?? "ring";

        try {
            localStorage.removeItem(this.FOCUS_STORAGE_KEY);
            localStorage.removeItem(this.FOCUS_MOTION_STORAGE_KEY);
            localStorage.removeItem(this.FOCUS_REVEAL_STORAGE_KEY);
            localStorage.removeItem(this.FOCUS_CUSTOM_STORAGE_KEY);
        } catch {
            /* storage may be unavailable */
        }
    },

    /**
     * Reports operating-system colour-scheme changes to .NET for as long as the page lives.
     *
     * @param dotNetRef The component to notify when the system scheme flips.
     */
    initSystemListener(dotNetRef: DotNetObjectReference): void {
        const query = window.matchMedia("(prefers-color-scheme: dark)");

        query.addEventListener("change", () => {
            void guardedInvoke(dotNetRef, "OnSystemThemeChanged", undefined, query.matches ? "DARK" : "LIGHT");
        });
    }
};

// ──────────────── ComponentUtils ────────────────
/** The click-outside listeners currently attached, keyed by the element they watch. */
const clickOutsideHandlers = new Map<string, (event: MouseEvent) => void>();

/** Focus, platform, click-outside, scroll-lock and clipboard helpers shared by the components. */
const componentUtils = {
    /**
     * Moves focus to an element by id, doing nothing when it is not in the document.
     *
     * @param elementId The id of the element to focus.
     */
    focusById(elementId: string): void {
        const el = document.getElementById(elementId);

        if (el) el.focus();
    },

    /**
     * Moves focus to the first focusable descendant of a container.
     *
     * @param containerSelector A CSS selector matching the container.
     */
    focusFirstElement(containerSelector: string): void {
        const container = document.querySelector(containerSelector);

        if (!container) return;

        const focusable = container.querySelector<HTMLElement>(
            'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );

        if (focusable) focusable.focus();
    },

    /**
     * Reports the host platform as "macos", "windows", "linux" or "".
     *
     * Callers use it to pick platform-appropriate keyboard glyphs; an empty result means "could not
     * tell", not "not macOS".
     *
     * @returns The platform family, or an empty string when it cannot be determined.
     */
    getPlatform(): string {
        const hinted = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform;
        const raw = (hinted || navigator.platform || "").toLowerCase();

        if (!raw) return "";
        // iPadOS reports "MacIntel" with a touch-capable pointer; both belong to the Command family.
        if (raw.includes("mac") || raw.includes("iphone") || raw.includes("ipad")) return "macos";
        if (raw.includes("win")) return "windows";
        if (raw.includes("linux") || raw.includes("x11") || raw.includes("android")) return "linux";

        return "";
    },

    /**
     * Reports document clicks that land outside an element to .NET.
     *
     * The listener is retained under the element's id so removeClickOutside can take it off again.
     *
     * @param dotNetRef The component to notify.
     * @param elementId The id of the element clicks must fall outside of.
     * @param callbackMethod The name of the [JSInvokable] method to call.
     */
    onClickOutside(dotNetRef: DotNetObjectReference, elementId: string, callbackMethod: string): void {
        const handler = (event: MouseEvent): void => {
            const el = document.getElementById(elementId);

            if (el && !el.contains(event.target as Node | null)) {
                void guardedInvoke(dotNetRef, callbackMethod, () => clickOutsideHandlers.has(elementId));
            }
        };

        document.addEventListener("click", handler);
        clickOutsideHandlers.set(elementId, handler);
    },

    /**
     * Detaches the click-outside listener for an element. Safe to call for an unknown id.
     *
     * @param elementId The id the listener was attached under.
     */
    removeClickOutside(elementId: string): void {
        const handler = clickOutsideHandlers.get(elementId);

        if (handler) {
            document.removeEventListener("click", handler);
            clickOutsideHandlers.delete(elementId);
        }
    },

    /** Suppresses page scrolling, for as long as an overlay owns the viewport. */
    lockScroll(): void {
        document.body.style.overflow = "hidden";
    },

    /** Restores page scrolling. */
    unlockScroll(): void {
        document.body.style.overflow = "";
    },

    /**
     * Writes text to the system clipboard.
     *
     * The answer is reported rather than assumed. A clipboard write needs a secure context and has to
     * arrive within the interaction that asked for it, so a refusal is an ordinary outcome and the caller
     * has to be able to tell it from a write that happened.
     *
     * @param text The payload to write.
     * @returns Whether the browser accepted the write.
     */
    async copyText(text: string): Promise<boolean> {
        if (typeof navigator === "undefined" || !navigator.clipboard) {
            return false;
        }

        try {
            await navigator.clipboard.writeText(text);

            return true;
        } catch {
            // A refusal is the browser declining to write, which the caller reports as nothing copied.
            return false;
        }
    }
};

// ──────────────── QueryState ────────────────
/** Keeps the address bar in step with component state without adding history entries. */
const queryState = {
    /**
     * Replaces the current address-bar URL, leaving the history entry and its state intact.
     *
     * @param url The absolute URL to show. An empty or non-string value, or the URL already shown,
     *            is ignored.
     */
    replaceUrl(url: string): void {
        if (typeof url !== "string" || !url.length) {
            return;
        }

        if (window.location.href === url) {
            return;
        }

        window.history.replaceState(window.history.state, "", url);
    }
};

// ──────────────── NotificationUtils ────────────────
/** Browser notification permission and display, degrading where the API is absent. */
const notificationUtils = {
    /**
     * Asks the browser for notification permission.
     *
     * @returns The resulting permission, or "denied" where the API is unavailable.
     */
    requestPermission(): Promise<NotificationPermission> {
        if ("Notification" in window) {
            return Notification.requestPermission();
        }

        return Promise.resolve<NotificationPermission>("denied");
    },

    /**
     * Shows a browser notification, doing nothing unless permission has been granted.
     *
     * @param title The notification title.
     * @param message The notification body.
     * @param icon The icon URL, or null to use the default.
     */
    showBrowserNotification(title: string, message: string, icon: string | null): void {
        if ("Notification" in window && Notification.permission === "granted") {
            new Notification(title, { body: message, icon: icon || "/favicon.png" });
        }
    }
};

// ──────────────── OtpInput ────────────────
/** What one OTP container registration retains, so its listeners can be taken off again. */
interface OtpRegistration {
    /** The container the listeners are attached to. */
    readonly container: HTMLElement;

    /** Suppresses the native paste and forwards the clipboard text to .NET. */
    readonly pasteHandler: (event: ClipboardEvent) => void;

    /** Selects a cell's content as focus enters it. */
    readonly focusHandler: (event: FocusEvent) => void;

    /**
     * The component the paste is reported to.
     *
     * Retained for the life of the registration and never disposed here: .NET owns the reference's
     * lifetime and releases it when the component is torn down.
     */
    readonly dotNetRef: DotNetObjectReference;
}

/**
 * The live OTP registrations, keyed by the container id .NET registered under.
 *
 * The disposer takes both delegated listeners off the container again.
 */
const otpRegistry = createElementRegistry<OtpRegistration>((entry) => {
    entry.container.removeEventListener("paste", entry.pasteHandler);
    entry.container.removeEventListener("focusin", entry.focusHandler);
});

/**
 * Reports a cell's position among its siblings.
 *
 * @param container The OTP container.
 * @param cell The cell to locate.
 * @returns The zero-based index, or 0 when the cell is not one of the container's cells.
 */
function indexOfCell(container: HTMLElement, cell: Element): number {
    const cells = container.querySelectorAll(".blz-otp__cell");

    for (let index = 0; index < cells.length; index++) {
        if (cells[index] === cell) return index;
    }

    return 0;
}

/** Cross-cell focus management and clipboard paste-split for BlazingOtpInput. */
const otpInput = {
    /**
     * Wires a delegated paste listener on the OTP container.
     *
     * On paste, the native action is suppressed and the clipboard text is forwarded to .NET to be
     * split across cells. Focus entering a cell selects its content so typing overwrites a filled cell.
     *
     * @param containerId The id of the OTP container element.
     * @param dotNetRef The component to forward pasted text to.
     */
    init(containerId: string, dotNetRef: DotNetObjectReference): void {
        const container = document.getElementById(containerId);

        if (!container) return;

        const pasteHandler = (event: ClipboardEvent): void => {
            const target = event.target as SelectableField | null;

            if (!target?.classList?.contains("blz-otp__cell")) {
                return;
            }

            const clipboard = event.clipboardData
                ?? (window as Window & { clipboardData?: DataTransfer }).clipboardData;
            const text = clipboard?.getData("text") ?? "";

            if (!text) return;

            event.preventDefault();

            const index = indexOfCell(container, target);

            void guardedInvoke(dotNetRef, "OnPasteFromJsAsync", () => otpRegistry.isLive(containerId), index, text);
        };

        // Select the cell's content on focus so typing overwrites a filled cell.
        const focusHandler = (event: FocusEvent): void => {
            const target = event.target as SelectableField | null;

            if (target?.classList?.contains("blz-otp__cell") && typeof target.select === "function") {
                target.select();
            }
        };

        container.addEventListener("paste", pasteHandler);
        container.addEventListener("focusin", focusHandler);
        otpRegistry.attach(containerId, container, { container, pasteHandler, focusHandler, dotNetRef });
    },

    /**
     * Moves focus to one cell and selects whatever it already holds.
     *
     * @param containerId The id of the OTP container element.
     * @param index The zero-based index of the cell to focus.
     */
    focusCell(containerId: string, index: number): void {
        const container = document.getElementById(containerId);

        if (!container) return;

        const cells = container.querySelectorAll<SelectableField>(".blz-otp__cell");
        const cell = cells[index];

        if (cell) {
            cell.focus();

            if (typeof cell.select === "function") cell.select();
        }
    },

    /**
     * Detaches an OTP container's listeners. Safe to call for an unknown id.
     *
     * @param containerId The id the container was registered under.
     */
    dispose(containerId: string): void {
        otpRegistry.detach(containerId);
    }
};

// ──────────────── SelectOnFocus ────────────────
/** Whether the delegated listeners have already been attached. */
let isSelectOnFocusInstalled = false;

/** The field a mousedown moved focus into, held until the matching mouseup settles the caret. */
let armedField: SelectableField | null = null;

/**
 * Library-wide "select all text on focus" behaviour for any editable field carrying the
 * data-blz-select-on-focus attribute.
 *
 * Delegated at the document level so it covers fields rendered later (inside popups, dialogs,
 * virtualized rows) with no per-element registration, and runs natively in the browser: doing this
 * from .NET would let the focusing click's mouseup collapse the selection before the round trip
 * completes.
 *
 * This is a public helper, not internal plumbing. Application code outside Blazing.UI can opt any
 * element in by adding the attribute in markup, toggle it at runtime with enable/disable, or select
 * a field imperatively with select(). Installed once when this module loads.
 */
const selectOnFocus = {
    /** The marker attribute an element carries to opt into the behaviour. */
    attribute: "data-blz-select-on-focus",

    /**
     * Selects the whole value of a text input or textarea. Safe on any element.
     *
     * @param element The field to select, or null when there is none.
     */
    select(element: SelectableField | null | undefined): void {
        if (element && typeof element.select === "function") {
            try {
                element.select();
            } catch {
                // The element was detached mid-focus; nothing to select.
            }
        }
    },

    /**
     * Opts an element in at runtime (equivalent to authoring the attribute in markup).
     *
     * @param element The element to opt in, or null when there is none.
     */
    enable(element: HTMLElement | null | undefined): void {
        if (element && typeof element.setAttribute === "function") {
            element.setAttribute(this.attribute, "");
        }
    },

    /**
     * Opts an element back out at runtime.
     *
     * @param element The element to opt out, or null when there is none.
     */
    disable(element: HTMLElement | null | undefined): void {
        if (element && typeof element.removeAttribute === "function") {
            element.removeAttribute(this.attribute);
        }
    },

    /**
     * Attaches the delegated listeners.
     *
     * Idempotent, so a host may call it explicitly when the module is loaded in a way that defers the
     * automatic call below.
     */
    install(): void {
        if (isSelectOnFocusInstalled) return;

        isSelectOnFocusInstalled = true;

        const selector = "[" + this.attribute + "]";
        const self = this;

        // Keyboard/tab focus selects immediately. A mouse click also selects here, but the caret
        // the browser places on mouseup would collapse it - the mouseup handler restores it.
        document.addEventListener("focusin", (event) => {
            const el = event.target as SelectableField | null;

            if (el && typeof el.matches === "function" && el.matches(selector)) {
                self.select(el);
            }
        });

        // Arm only when the click is what moves focus into the field, so a later caret-positioning
        // click inside an already-focused field still behaves normally.
        document.addEventListener("mousedown", (event) => {
            const target = event.target as SelectableField | null;
            const el = target && typeof target.closest === "function"
                ? target.closest<SelectableField>(selector)
                : null;

            armedField = el && document.activeElement !== el ? el : null;
        }, true);

        document.addEventListener("mouseup", () => {
            const el = armedField;

            armedField = null;

            if (!el) return;

            // Once the browser has settled the caret from this focusing click, re-select - unless
            // the user dragged out their own range, which is left alone.
            requestAnimationFrame(() => {
                if (document.activeElement === el && el.selectionStart === el.selectionEnd) {
                    self.select(el);
                }
            });
        });
    }
};

// ──────────────── Password ────────────────
/** The caps-lock listeners currently attached, keyed by the token startMonitor handed out. */
const capsLockMonitors = new Map<string, (event: KeyboardEvent) => void>();

/** Distinguishes one monitor's token from the next. */
let capsLockMonitorCounter = 0;

/**
 * Caps-lock detection for BlazingPassword.
 *
 * KeyboardEventArgs does not surface modifier state to .NET, so a document-level key listener reads
 * event.getModifierState("CapsLock") and reports changes to the owning component. Monitoring starts
 * on focus and stops on blur, so at most one listener pair is live per focused password field.
 */
const password = {
    /**
     * Attaches a capture-phase keydown/keyup pair and invokes OnCapsLockChanged on the .NET ref
     * whenever the Caps Lock state flips.
     *
     * @param dotNetRef The component to report caps-lock changes to.
     * @returns An opaque token for stopMonitor.
     */
    startMonitor(dotNetRef: DotNetObjectReference): string {
        capsLockMonitorCounter += 1;

        const token = "caps-" + capsLockMonitorCounter;

        let wasCapsLockOn: boolean | null = null;

        const handler = (event: KeyboardEvent): void => {
            if (typeof event.getModifierState !== "function") return;

            const isOn = event.getModifierState("CapsLock");

            if (isOn === wasCapsLockOn) return;

            wasCapsLockOn = isOn;

            void guardedInvoke(dotNetRef, "OnCapsLockChanged", () => capsLockMonitors.has(token), isOn);
        };

        document.addEventListener("keydown", handler, true);
        document.addEventListener("keyup", handler, true);
        capsLockMonitors.set(token, handler);

        return token;
    },

    /**
     * Detaches the listener pair for a token returned by startMonitor.
     *
     * Safe to call with an unknown or already-stopped token, so a late teardown is harmless.
     *
     * @param token The token startMonitor returned.
     */
    stopMonitor(token: string): void {
        const handler = capsLockMonitors.get(token);

        if (!handler) return;

        document.removeEventListener("keydown", handler, true);
        document.removeEventListener("keyup", handler, true);
        capsLockMonitors.delete(token);
    }
};

// ──────────────── NumericInput ────────────────
/** Whether the delegated listener has already been attached. */
let isNumericInputInstalled = false;

/**
 * Restricts a numeric field to the characters its owning component permits.
 *
 * Delegated at the document level so it covers fields rendered later (inside popups, dialogs, virtualized
 * rows) with no per-element registration, and driven entirely by the data-blz-numeric* attributes the
 * component writes: the accepted sign, decimal separator and group separator are resolved in .NET from
 * the current culture and the component's own parameters, so no numeric policy lives here.
 *
 * beforeinput is the hook rather than keydown because it is the only one that also covers pasting,
 * dropping and autofill, and because it can test the value the edit would produce instead of the single
 * character causing it - which is what refuses a second decimal point.
 *
 * This is an enhancement, not the enforcement: the component re-reads and corrects the field when the
 * entry is committed, so a page where this module never loaded still refuses a bad value.
 */
const numericInput = {
    /** The marker attribute a field carries to opt into the behaviour. */
    attribute: "data-blz-numeric",

    /**
     * Reports whether a field would still hold a permissible entry after an edit.
     *
     * @param field The field being edited.
     * @param value The value the edit would produce.
     * @returns True when the value may be entered.
     */
    accepts(field: HTMLInputElement, value: string): boolean {
        // An absent or empty fraction-digit attribute means the field sets no limit.
        const fractionDigits = Number.parseInt(field.getAttribute(this.attribute + "-fraction-digits") ?? "", 10);

        const pattern = buildNumericPattern(
            field.getAttribute(this.attribute + "-negative") ?? "",
            field.getAttribute(this.attribute + "-decimal") ?? "",
            field.getAttribute(this.attribute + "-group") ?? "",
            Number.isNaN(fractionDigits) ? undefined : fractionDigits);

        return pattern.test(value);
    },

    /**
     * Attaches the delegated listener.
     *
     * Idempotent, so a host may call it explicitly when the module is loaded in a way that defers the
     * automatic call below.
     */
    install(): void {
        if (isNumericInputInstalled) return;

        isNumericInputInstalled = true;

        const selector = "[" + this.attribute + "]";
        const self = this;

        document.addEventListener("beforeinput", (event) => {
            const field = event.target as HTMLInputElement | null;

            if (!field || typeof field.matches !== "function" || !field.matches(selector)) return;

            // Deletions, undo and redo are always allowed: they can only ever shorten the entry, and
            // blocking them would strand the user on text they cannot clear.
            if (!event.inputType.startsWith("insert")) return;

            // A paste carries its text on the transfer object rather than in data on some engines.
            const inserted = event.data ?? event.dataTransfer?.getData("text") ?? "";
            const start = field.selectionStart ?? field.value.length;
            const end = field.selectionEnd ?? start;
            const next = field.value.slice(0, start) + inserted + field.value.slice(end);

            if (!self.accepts(field, next)) {
                event.preventDefault();
            }
        });

        // Wheel stepping. Only a focused field that carries the wheel marker - written by .NET while
        // AllowMouseWheel is on and the field is editable - reacts, and only to a turn over its own field box;
        // every other wheel turn is left alone, so the page always scrolls under an unfocused field. The
        // listener is not passive because stopping the page scrolling is the point. Turns are summed and
        // reported at most once per animation frame, so a trackpad's stream of small deltas costs one call
        // into .NET per frame rather than one per event.
        const wheelSelector = "[" + this.attribute + "-wheel]";
        let pendingTurns = 0;
        let frame = 0;

        document.addEventListener("wheel", (event) => {
            const field = document.activeElement;

            if (!(field instanceof HTMLInputElement) || !field.matches(wheelSelector)) return;

            const box = field.parentElement;

            if (!box || !(event.target instanceof Node) || !box.contains(event.target)) return;

            event.preventDefault();

            // A turn up scrolls content up (a negative delta), which steps the value up.
            pendingTurns -= Math.sign(event.deltaY);

            if (frame !== 0) return;

            frame = requestAnimationFrame(() => {
                frame = 0;

                const direction = Math.sign(pendingTurns);

                pendingTurns = 0;

                // Re-checked at dispatch time: focus may have left the field during the frame. The event
                // bubbles because Blazor listens for custom event types at its root, not on the field.
                if (direction !== 0 && document.activeElement === field) {
                    field.dispatchEvent(new CustomEvent("blznumericwheel", { bubbles: true, detail: { direction } }));
                }
            });
        }, { passive: false });
    }
};

/**
 * The shape published on the global object.
 *
 * Every one of these names is addressed from C# as part of a string literal, so the group names keep
 * their PascalCase spelling and the members keep theirs.
 */
export interface BlazingCommonApi {
    /** General-purpose element, layout and interop helpers. */
    Utils: typeof utils;

    /** The priming re-entrancy guard the browser modules share. */
    Interop: typeof interop;

    /** Viewport-width reporting. */
    ResponsiveService: typeof responsiveService;

    /** Theme, accent, palette and background application and persistence. */
    ThemeService: typeof themeService;

    /** Focus, platform, click-outside, scroll-lock and clipboard helpers. */
    ComponentUtils: typeof componentUtils;

    /** Address-bar updates that add no history entry. */
    QueryState: typeof queryState;

    /** Browser notification permission and display. */
    NotificationUtils: typeof notificationUtils;

    /** Cross-cell focus and paste handling for the one-time-password input. */
    OtpInput: typeof otpInput;

    /** Select-all-on-focus behaviour for opted-in fields. */
    SelectOnFocus: typeof selectOnFocus;

    /** Character restriction for opted-in numeric fields. */
    NumericInput: typeof numericInput;

    /** Caps-lock monitoring for password fields. */
    Password: typeof password;
}

declare global {
    interface Window {
        /**
         * The shared browser helpers, published by this module and addressed from C# by name.
         *
         * Undefined until the module has run, which is why a host page must load it with
         * `<script type="module">`.
         */
        BlazingCommon?: BlazingCommonApi;
    }
}

// Merged onto whatever is already there rather than replacing it, so a host that published its own
// SelectOnFocus or Password before this module ran keeps the one it installed.
const existing: Partial<BlazingCommonApi> = window.BlazingCommon ?? {};
const selectOnFocusApi = existing.SelectOnFocus ?? selectOnFocus;
const numericInputApi = existing.NumericInput ?? numericInput;

window.BlazingCommon = Object.assign(existing, {
    Utils: utils,
    Interop: interop,
    ResponsiveService: responsiveService,
    ThemeService: themeService,
    ComponentUtils: componentUtils,
    QueryState: queryState,
    NotificationUtils: notificationUtils,
    OtpInput: otpInput,
    SelectOnFocus: selectOnFocusApi,
    NumericInput: numericInputApi,
    Password: existing.Password ?? password
});

selectOnFocusApi.install();
numericInputApi.install();

// A region that handles some keys itself declares them in data-blz-guard-keys (space-separated), and their
// default action - page scroll, for the navigation keys - is suppressed as they are pressed inside it. One
// listener on the document serves every such region, so the guard holds from the region's first render, with
// no interop call that could arrive after the first key press.
document.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || !(event.target instanceof Element)) {
        return;
    }

    const region = event.target.closest<HTMLElement>("[data-blz-guard-keys]");
    if (region?.dataset["blzGuardKeys"]?.split(" ").includes(event.key)) {
        event.preventDefault();
    }
});

// A range input that declares data-blz-large-step moves by that amount on PageUp and PageDown instead of the
// browser's own page increment. The move happens here and is reported as the native input and change events, so
// the component hears a page key exactly as it hears an arrow key, and no key event crosses to .NET. A read-only
// input keeps its value. Served by one document listener, so it holds from the input's first render.
document.addEventListener("keydown", (event) => {
    if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey
        || (event.key !== "PageUp" && event.key !== "PageDown")
        || !(event.target instanceof HTMLInputElement) || event.target.type !== "range") {
        return;
    }

    const input = event.target;
    const largeStep = Number(input.dataset["blzLargeStep"]);
    if (!Number.isFinite(largeStep) || largeStep <= 0) {
        return;
    }

    event.preventDefault();
    if (input.disabled || input.hasAttribute("readonly")) {
        return;
    }

    const current = Number(input.value);
    const moved = event.key === "PageUp" ? current + largeStep : current - largeStep;
    const next = Math.min(Number(input.max), Math.max(Number(input.min), Number(moved.toPrecision(15))));
    if (next === current) {
        return;
    }

    input.value = String(next);
    input.dispatchEvent(new Event("input", { bubbles: true }));
    input.dispatchEvent(new Event("change", { bubbles: true }));
});
